import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  BookOpen, Plus, Search, FileText, Award, Briefcase, 
  Lightbulb, Users, TrendingUp, Calendar, Edit, Trash2,
  RefreshCw, ExternalLink, IndianRupee, GraduationCap, Download, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL paths to tab values
const getTabFromPath = (pathname) => {
  if (pathname.includes('/research/projects')) return 'projects';
  if (pathname.includes('/research/patents')) return 'patents';
  if (pathname.includes('/research/workshops')) return 'workshops';
  return 'publications';
};

export default function FacultyResearch() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [publications, setPublications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [patents, setPatents] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [showPubModal, setShowPubModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showPatentModal, setShowPatentModal] = useState(false);
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  
  const [pubForm, setPubForm] = useState({
    title: '', authors: '', publication_type: 'journal', journal_name: '',
    conference_name: '', publisher: '', volume: '', issue: '', pages: '',
    year: new Date().getFullYear(), doi: '', impact_factor: '',
    is_scopus_indexed: false, is_wos_indexed: false, is_ugc_care_listed: false,
    abstract: '', keywords: ''
  });
  
  const [projectForm, setProjectForm] = useState({
    title: '', principal_investigator: '', co_investigators: '',
    funding_agency: '', project_type: 'major', sanctioned_amount: '',
    received_amount: '', start_date: '', end_date: '', status: 'ongoing', description: ''
  });
  
  const [patentForm, setPatentForm] = useState({
    title: '', inventors: '', patent_number: '', application_number: '',
    filing_date: '', grant_date: '', status: 'filed', patent_office: 'Indian', description: ''
  });
  
  const [workshopForm, setWorkshopForm] = useState({
    title: '', type: 'workshop', role: 'organized', organizer: '',
    venue: '', start_date: '', end_date: '', participants_count: '',
    faculty_names: '', description: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    fetchStats();
    fetchData();
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faculty-research/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const handleExport = async (format) => {
    try {
      toast.loading(`Generating ${format.toUpperCase()} report...`);
      const response = await axios.get(
        `${API_URL}/api/naac-export/faculty-research/${format}`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Faculty_Research_Report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.dismiss();
      toast.success('Report downloaded successfully!');
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to generate report');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'publications') {
        const res = await axios.get(`${API_URL}/api/faculty-research/publications`, { headers });
        setPublications(res.data.publications || []);
      } else if (activeTab === 'projects') {
        const res = await axios.get(`${API_URL}/api/faculty-research/projects`, { headers });
        setProjects(res.data.projects || []);
      } else if (activeTab === 'patents') {
        const res = await axios.get(`${API_URL}/api/faculty-research/patents`, { headers });
        setPatents(res.data.patents || []);
      } else if (activeTab === 'workshops') {
        const res = await axios.get(`${API_URL}/api/faculty-research/workshops`, { headers });
        setWorkshops(res.data.workshops || []);
      }
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handlePubSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...pubForm,
        authors: pubForm.authors.split(',').map(a => a.trim()),
        keywords: pubForm.keywords ? pubForm.keywords.split(',').map(k => k.trim()) : [],
        year: parseInt(pubForm.year),
        impact_factor: pubForm.impact_factor ? parseFloat(pubForm.impact_factor) : null
      };
      await axios.post(`${API_URL}/api/faculty-research/publications`, payload, { headers });
      toast.success('Publication added');
      setShowPubModal(false);
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add publication');
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...projectForm,
        co_investigators: projectForm.co_investigators ? projectForm.co_investigators.split(',').map(c => c.trim()) : [],
        sanctioned_amount: parseFloat(projectForm.sanctioned_amount),
        received_amount: projectForm.received_amount ? parseFloat(projectForm.received_amount) : 0
      };
      await axios.post(`${API_URL}/api/faculty-research/projects`, payload, { headers });
      toast.success('Project added');
      setShowProjectModal(false);
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add project');
    }
  };

  const handlePatentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...patentForm,
        inventors: patentForm.inventors.split(',').map(i => i.trim())
      };
      await axios.post(`${API_URL}/api/faculty-research/patents`, payload, { headers });
      toast.success('Patent added');
      setShowPatentModal(false);
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add patent');
    }
  };

  const handleWorkshopSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...workshopForm,
        faculty_names: workshopForm.faculty_names ? workshopForm.faculty_names.split(',').map(f => f.trim()) : [],
        participants_count: workshopForm.participants_count ? parseInt(workshopForm.participants_count) : null
      };
      await axios.post(`${API_URL}/api/faculty-research/workshops`, payload, { headers });
      toast.success('Workshop added');
      setShowWorkshopModal(false);
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add workshop');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/faculty-research/${type}/${id}`, { headers });
      toast.success('Deleted successfully');
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const pubTypeColors = {
    journal: 'bg-blue-100 text-blue-800',
    conference: 'bg-green-100 text-green-800',
    book: 'bg-purple-100 text-purple-800',
    book_chapter: 'bg-orange-100 text-orange-800',
    patent: 'bg-red-100 text-red-800'
  };

  return (
    <div className="p-6 space-y-6" data-testid="faculty-research-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faculty Research</h1>
          <p className="text-gray-500">Manage publications, projects, patents & workshops</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => handleExport('excel')}>
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')}>
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" onClick={() => setShowPubModal(true)}>
            <BookOpen className="h-4 w-4 mr-1" /> Publication
          </Button>
          <Button variant="outline" onClick={() => setShowProjectModal(true)}>
            <Briefcase className="h-4 w-4 mr-1" /> Project
          </Button>
          <Button variant="outline" onClick={() => setShowPatentModal(true)}>
            <Lightbulb className="h-4 w-4 mr-1" /> Patent
          </Button>
          <Button onClick={() => setShowWorkshopModal(true)}>
            <Users className="h-4 w-4 mr-1" /> Workshop
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard title="Publications" value={stats.publications?.total || 0} icon={BookOpen} color="blue" />
          <StatCard title="Scopus" value={stats.publications?.scopus_indexed || 0} icon={Award} color="green" />
          <StatCard title="Projects" value={stats.projects?.total || 0} icon={Briefcase} color="purple" />
          <StatCard 
            title="Funding" 
            value={`₹${((stats.projects?.total_funding_sanctioned || 0)/100000).toFixed(1)}L`} 
            icon={IndianRupee} 
            color="green"
          />
          <StatCard title="Patents" value={stats.patents?.total || 0} icon={Lightbulb} color="orange" />
          <StatCard title="Workshops" value={stats.workshops?.total || 0} icon={Users} color="cyan" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="publications">Publications</TabsTrigger>
          <TabsTrigger value="projects">Research Projects</TabsTrigger>
          <TabsTrigger value="patents">Patents</TabsTrigger>
          <TabsTrigger value="workshops">Workshops/FDPs</TabsTrigger>
        </TabsList>

        {/* Publications Tab */}
        <TabsContent value="publications" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-left">Authors</th>
                      <th className="px-4 py-3 text-center">Type</th>
                      <th className="px-4 py-3 text-center">Year</th>
                      <th className="px-4 py-3 text-center">Indexing</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="6" className="text-center py-8"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                    ) : publications.length === 0 ? (
                      <tr><td colSpan="6" className="text-center py-8 text-gray-500">No publications found</td></tr>
                    ) : (
                      publications.map((pub) => (
                        <tr key={pub.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium">{pub.title}</p>
                            <p className="text-xs text-gray-500">{pub.journal_name || pub.conference_name}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">{pub.authors?.join(', ')}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${pubTypeColors[pub.publication_type]}`}>
                              {pub.publication_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">{pub.year}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-1 justify-center">
                              {pub.is_scopus_indexed && <span className="px-1 py-0.5 bg-green-100 text-green-700 text-xs rounded">Scopus</span>}
                              {pub.is_wos_indexed && <span className="px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">WoS</span>}
                              {pub.is_ugc_care_listed && <span className="px-1 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">UGC</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm" onClick={() => handleDelete('publications', pub.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.length === 0 ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center text-gray-500">No projects found</CardContent></Card>
            ) : (
              projects.map((project) => (
                <Card key={project.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between">
                      <CardTitle className="text-lg">{project.title}</CardTitle>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        project.status === 'ongoing' ? 'bg-green-100 text-green-800' :
                        project.status === 'completed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
                      }`}>{project.status}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p><strong>PI:</strong> {project.principal_investigator}</p>
                    <p><strong>Agency:</strong> {project.funding_agency}</p>
                    <p><strong>Amount:</strong> ₹{project.sanctioned_amount?.toLocaleString('en-IN')}</p>
                    <div className="flex justify-between pt-2">
                      <span className="text-gray-500">{project.start_date} - {project.end_date || 'Ongoing'}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete('projects', project.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Patents Tab */}
        <TabsContent value="patents" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {patents.length === 0 ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center text-gray-500">No patents found</CardContent></Card>
            ) : (
              patents.map((patent) => (
                <Card key={patent.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{patent.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p><strong>Inventors:</strong> {patent.inventors?.join(', ')}</p>
                    <p><strong>Status:</strong> <span className={`px-2 py-0.5 rounded ${
                      patent.status === 'granted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>{patent.status}</span></p>
                    {patent.patent_number && <p><strong>Patent #:</strong> {patent.patent_number}</p>}
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete('patents', patent.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Workshops Tab */}
        <TabsContent value="workshops" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-center">Type</th>
                      <th className="px-4 py-3 text-center">Role</th>
                      <th className="px-4 py-3 text-left">Venue</th>
                      <th className="px-4 py-3 text-center">Date</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workshops.length === 0 ? (
                      <tr><td colSpan="6" className="text-center py-8 text-gray-500">No workshops found</td></tr>
                    ) : (
                      workshops.map((ws) => (
                        <tr key={ws.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{ws.title}</td>
                          <td className="px-4 py-3 text-center capitalize">{ws.type}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              ws.role === 'organized' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                            }`}>{ws.role}</span>
                          </td>
                          <td className="px-4 py-3">{ws.venue}</td>
                          <td className="px-4 py-3 text-center">{ws.start_date}</td>
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm" onClick={() => handleDelete('workshops', ws.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Publication Modal */}
      <Dialog open={showPubModal} onOpenChange={setShowPubModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Publication</DialogTitle></DialogHeader>
          <form onSubmit={handlePubSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input value={pubForm.title} onChange={(e) => setPubForm({...pubForm, title: e.target.value})} required />
            </div>
            <div>
              <label className="text-sm font-medium">Authors * (comma separated)</label>
              <Input value={pubForm.authors} onChange={(e) => setPubForm({...pubForm, authors: e.target.value})} placeholder="Dr. John, Dr. Smith" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Type *</label>
                <Select value={pubForm.publication_type} onValueChange={(v) => setPubForm({...pubForm, publication_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="journal">Journal Article</SelectItem>
                    <SelectItem value="conference">Conference Paper</SelectItem>
                    <SelectItem value="book">Book</SelectItem>
                    <SelectItem value="book_chapter">Book Chapter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Year *</label>
                <Input type="number" value={pubForm.year} onChange={(e) => setPubForm({...pubForm, year: e.target.value})} required />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Journal/Conference Name</label>
              <Input value={pubForm.journal_name} onChange={(e) => setPubForm({...pubForm, journal_name: e.target.value})} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Volume</label>
                <Input value={pubForm.volume} onChange={(e) => setPubForm({...pubForm, volume: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Issue</label>
                <Input value={pubForm.issue} onChange={(e) => setPubForm({...pubForm, issue: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Pages</label>
                <Input value={pubForm.pages} onChange={(e) => setPubForm({...pubForm, pages: e.target.value})} placeholder="1-10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">DOI</label>
                <Input value={pubForm.doi} onChange={(e) => setPubForm({...pubForm, doi: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Impact Factor</label>
                <Input type="number" step="0.01" value={pubForm.impact_factor} onChange={(e) => setPubForm({...pubForm, impact_factor: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <Checkbox checked={pubForm.is_scopus_indexed} onCheckedChange={(c) => setPubForm({...pubForm, is_scopus_indexed: c})} />
                Scopus Indexed
              </label>
              <label className="flex items-center gap-2">
                <Checkbox checked={pubForm.is_wos_indexed} onCheckedChange={(c) => setPubForm({...pubForm, is_wos_indexed: c})} />
                Web of Science
              </label>
              <label className="flex items-center gap-2">
                <Checkbox checked={pubForm.is_ugc_care_listed} onCheckedChange={(c) => setPubForm({...pubForm, is_ugc_care_listed: c})} />
                UGC Care Listed
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPubModal(false)}>Cancel</Button>
              <Button type="submit">Add Publication</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Modal */}
      <Dialog open={showProjectModal} onOpenChange={setShowProjectModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Research Project</DialogTitle></DialogHeader>
          <form onSubmit={handleProjectSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Project Title *</label>
              <Input value={projectForm.title} onChange={(e) => setProjectForm({...projectForm, title: e.target.value})} required />
            </div>
            <div>
              <label className="text-sm font-medium">Principal Investigator *</label>
              <Input value={projectForm.principal_investigator} onChange={(e) => setProjectForm({...projectForm, principal_investigator: e.target.value})} required />
            </div>
            <div>
              <label className="text-sm font-medium">Co-Investigators (comma separated)</label>
              <Input value={projectForm.co_investigators} onChange={(e) => setProjectForm({...projectForm, co_investigators: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium">Funding Agency *</label>
              <Input value={projectForm.funding_agency} onChange={(e) => setProjectForm({...projectForm, funding_agency: e.target.value})} placeholder="UGC, DST, AICTE..." required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Sanctioned Amount (₹) *</label>
                <Input type="number" value={projectForm.sanctioned_amount} onChange={(e) => setProjectForm({...projectForm, sanctioned_amount: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={projectForm.status} onValueChange={(v) => setProjectForm({...projectForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Date *</label>
                <Input type="date" value={projectForm.start_date} onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={projectForm.end_date} onChange={(e) => setProjectForm({...projectForm, end_date: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProjectModal(false)}>Cancel</Button>
              <Button type="submit">Add Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Patent Modal */}
      <Dialog open={showPatentModal} onOpenChange={setShowPatentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Patent</DialogTitle></DialogHeader>
          <form onSubmit={handlePatentSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Patent Title *</label>
              <Input value={patentForm.title} onChange={(e) => setPatentForm({...patentForm, title: e.target.value})} required />
            </div>
            <div>
              <label className="text-sm font-medium">Inventors * (comma separated)</label>
              <Input value={patentForm.inventors} onChange={(e) => setPatentForm({...patentForm, inventors: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Application Number</label>
                <Input value={patentForm.application_number} onChange={(e) => setPatentForm({...patentForm, application_number: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Patent Number</label>
                <Input value={patentForm.patent_number} onChange={(e) => setPatentForm({...patentForm, patent_number: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={patentForm.status} onValueChange={(v) => setPatentForm({...patentForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="filed">Filed</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="granted">Granted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Filing Date</label>
                <Input type="date" value={patentForm.filing_date} onChange={(e) => setPatentForm({...patentForm, filing_date: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPatentModal(false)}>Cancel</Button>
              <Button type="submit">Add Patent</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Workshop Modal */}
      <Dialog open={showWorkshopModal} onOpenChange={setShowWorkshopModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Workshop/Seminar/FDP</DialogTitle></DialogHeader>
          <form onSubmit={handleWorkshopSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input value={workshopForm.title} onChange={(e) => setWorkshopForm({...workshopForm, title: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={workshopForm.type} onValueChange={(v) => setWorkshopForm({...workshopForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="seminar">Seminar</SelectItem>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="fdp">FDP</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Role</label>
                <Select value={workshopForm.role} onValueChange={(v) => setWorkshopForm({...workshopForm, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="organized">Organized</SelectItem>
                    <SelectItem value="attended">Attended</SelectItem>
                    <SelectItem value="presented">Presented</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Venue</label>
              <Input value={workshopForm.venue} onChange={(e) => setWorkshopForm({...workshopForm, venue: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Date *</label>
                <Input type="date" value={workshopForm.start_date} onChange={(e) => setWorkshopForm({...workshopForm, start_date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={workshopForm.end_date} onChange={(e) => setWorkshopForm({...workshopForm, end_date: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Participants Count</label>
              <Input type="number" value={workshopForm.participants_count} onChange={(e) => setWorkshopForm({...workshopForm, participants_count: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowWorkshopModal(false)}>Cancel</Button>
              <Button type="submit">Add Workshop</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
